import React from 'react';
import { Title, Loader, ReloadIconButton, TablePaginationWrapper } from '../../elements';
import { InjectionHistoryResult } from '../../models/injection';
import { InjectionsService } from '../../hooks/InjectionsService';
import { notifications } from '../../utils';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Chip from '@material-ui/core/Chip';
import LocalHospitalIcon from '@material-ui/icons/LocalHospital';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE, LoaderSize } from '../../constants/constants';
import NoDataMessage from '../shared/NoDataMessage';
import { useStyles } from './styles';
import { useTranslation } from 'react-i18next';
import { useFarmDateTime } from '../../hooks/useFarmDateTime';

interface InjectionHistoryViewProps {
  entityId: string;
  onReload?: () => void;
}

/** What a ref to this view can call. */
export interface InjectionHistoryViewHandle {
  reload: () => void;
}

const InjectionHistoryView = React.forwardRef(({ entityId, onReload }: InjectionHistoryViewProps, ref) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { formatDate, formatTime } = useFarmDateTime();

  const injectionsService = new InjectionsService();

  const [searchResult, setSearchResult] = React.useState([] as InjectionHistoryResult[]);
  const [isBusy, setIsBusy] = React.useState(false);
  const [shouldCallReload, setShouldCallReload] = React.useState(true);

  // pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);
  const [total, setTotal] = React.useState(0);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setShouldCallReload(true);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setShouldCallReload(true);
  };
  // * pagination

  //reset data when animal id changes
  React.useEffect(() => {
    setSearchResult([] as InjectionHistoryResult[]);
    setTotal(0);
    setPage(0);

    setShouldCallReload(true);
  }, [entityId]);

  //reload injections hook call
  React.useEffect(() => {
    if (shouldCallReload) {
      loadInjectionHistory();
      setShouldCallReload(false);
    }
  }, [shouldCallReload]);

  //external reload call
  React.useImperativeHandle(
    ref,
    (): InjectionHistoryViewHandle => ({
      reload() {
        setShouldCallReload(true);
      }
    })
  );

  const loadInjectionHistory = async () => {
    setIsBusy(true);

    const searchPage = page === 0 ? 1 : page + 1;

    injectionsService
      .getInjectionHistory(entityId, searchPage, rowsPerPage)
      .then((result) => {
        setSearchResult(result.data);
        setTotal(result.total);
      })
      .catch((e) => {
        notifications.error(e?.error?.message ?? e.toString());
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const renderWaitingTime = (waitingTime: number) => {
    return waitingTime === 0 ? '-' : t('f-com-number-days', { number: waitingTime });
  };

  const renderDose = (dose: number) => {
    return dose === 0 ? '-' : `${dose.toFixed(2)} cc`;
  };
  const handleReloadClick = () => {
    loadInjectionHistory();
    if (onReload) {
      onReload();
    }
  };

  return (
    <>
      <Paper className={classes.paper} elevation={1}>
        <Grid container spacing={1}>
          <Grid container direction="row">
            <Grid container item spacing={1} xs={10}>
              <Grid item>
                <LocalHospitalIcon color="primary" fontSize="large" />
              </Grid>
              <Grid item>
                <Title>{t('f-injections-view-title')}</Title>
              </Grid>
              <Grid item>
                <Chip label={total} variant="outlined" />
              </Grid>
            </Grid>
            <Grid item xs={2} className={classes.loadInjectionsBtnGrid}>
              <ReloadIconButton action={handleReloadClick} />
            </Grid>
          </Grid>

          <Grid item xs={12}>
            {!isBusy && searchResult && searchResult.length > 0 ? (
              <TablePaginationWrapper
                rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              >
                <Paper>
                  <Table size="small">
                    <TableHead className={classes.tableHeader}>
                      <TableRow>
                        <TableCell>{t('f-com-date')}</TableCell>
                        <TableCell>{t('f-com-time')}</TableCell>
                        <TableCell>{t('f-employee-or-veterinary')}</TableCell>
                        <TableCell>{t('f-com-age')}</TableCell>
                        <TableCell>{t('f-com-dose')}</TableCell>
                        <TableCell>{t('f-com-supply')}</TableCell>
                        <TableCell>{t('f-com-lot')}</TableCell>
                        <TableCell>{t('f-com-treat-num')}</TableCell>
                        <TableCell>{t('f-com-withdrawal')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {searchResult.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell>{formatDate(row.dateTime)}</TableCell>
                          <TableCell>{formatTime(row.dateTime)}</TableCell>
                          <TableCell>
                            {row.employee?.name || '-'} / {row.veterinary?.name || '-'}
                          </TableCell>
                          <TableCell>{t('f-com-number-days', { number: row.dayNumber })}</TableCell>
                          <TableCell>{renderDose(row.dose)}</TableCell>
                          <TableCell>{row.medicalSupply.number || '-'}</TableCell>
                          <TableCell>{row.lotNumber || '-'}</TableCell>
                          <TableCell>{row.treatmentNumber || '-'}</TableCell>
                          <TableCell>{renderWaitingTime(row.waitingTime)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              </TablePaginationWrapper>
            ) : (
              !isBusy && <NoDataMessage text={t('f-no-data-to-display')} />
            )}
            <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />
          </Grid>
        </Grid>
      </Paper>
    </>
  );
});

export default InjectionHistoryView;
